import { ReactNode, useState } from 'react';
import {
  Table,
  Button,
  Input,
  Space,
  Modal,
  Form,
  Popconfirm,
  Tag,
  Upload,
  App as AntdApp,
  Switch,
  Tooltip,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Plus,
  Pencil,
  Trash2,
  Upload as UploadIcon,
  Download,
  Search,
  FileSpreadsheet,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import type { BaseCategoryItem, ImportResult } from '@/types/categories';

/**
 * V1.0.33: Helper transform columns thông thường thành columns tương thích với mode gom nhóm.
 * Mỗi column sẽ check `record.__isGroup` — nếu true thì:
 * - Column đầu tiên render group header (label + count + color)
 * - Các column khác render trống (colspan 0)
 */
function buildGroupedColumns<T extends BaseCategoryItem>(
  cols: ColumnsType<T>,
  actionColumn: ColumnsType<T>[number]
): ColumnsType<any> {
  return [...cols, actionColumn].map((col, idx) => ({
    ...(col as any),
    onCell: (record: any) => {
      if (record.__isGroup) {
        return idx === 0
          ? { colSpan: cols.length + 1 }  // Col đầu chiếm hết
          : { colSpan: 0 };                // Các col còn lại ẩn
      }
      return {};
    },
    render: (val: any, record: any, rowIdx: number) => {
      if (record.__isGroup && idx === 0) {
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '4px 0',
          }}>
            {record.__groupColor && (
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: record.__groupColor,
                display: 'inline-block',
              }} />
            )}
            <span style={{
              fontWeight: 700, fontSize: 13,
              color: record.__groupColor ?? '#1A1E23',
            }}>
              {record.__groupLabel}
            </span>
            <span style={{
              fontSize: 11, color: '#7d8a98',
              background: '#f0f4f8', padding: '1px 8px',
              borderRadius: 10,
            }}>
              {record.__groupCount} khoa
            </span>
          </div>
        );
      }
      if (record.__isGroup) return null;
      const originalRender = (col as any).render;
      if (originalRender) return originalRender(val, record, rowIdx);
      return val;
    },
  }));
}

export interface CategoryFormProps<T> {
  initialValues?: Partial<T>;
  onFinish: (values: T) => void;
  isSystem?: boolean;
}

export interface GenericCategoryPageProps<T extends BaseCategoryItem> {
  title: string;
  subtitle?: string;
  breadcrumb?: string;
  icon?: ReactNode;
  data: T[];
  columns: ColumnsType<T>;
  renderForm: (props: CategoryFormProps<T>) => ReactNode;
  onCreate: (item: Partial<T>) => Promise<void>;
  onUpdate: (id: string, item: Partial<T>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onImport?: (file: File) => Promise<ImportResult>;
  onExport?: () => void;
  searchKeys?: (keyof T)[];
  extraActions?: ReactNode;
  /**
   * V1.0.33: Cho phép gom data theo 1 field — render group header trên mỗi nhóm.
   * Trả về object { key, label, color } để chỉ định nhóm + nhãn hiển thị.
   * Nếu trả null → row vào nhóm "__none__" (Chưa phân loại).
   */
  groupBy?: (item: T) => { key: string; label: string; color?: string } | null;
  /** Toggle bật/tắt gom nhóm (UI sẽ có Switch). Default true nếu groupBy được set. */
  enableGroupingToggle?: boolean;
}

export function GenericCategoryPage<T extends BaseCategoryItem>({
  title,
  subtitle,
  breadcrumb,
  data,
  columns,
  renderForm,
  onCreate,
  onUpdate,
  onDelete,
  onImport,
  onExport,
  searchKeys = ['code' as keyof T, 'name' as keyof T],
  extraActions,
  groupBy,
  enableGroupingToggle = true,
}: GenericCategoryPageProps<T>) {
  const { message } = AntdApp.useApp();
  const [search, setSearch] = useState('');
  // V1.0.33: state cho grouping toggle — default = bật nếu có groupBy
  const [groupingEnabled, setGroupingEnabled] = useState(!!groupBy);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);

  // Filter
  const filteredData = data.filter((item) => {
    if (showOnlyActive && !item.isActive) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return searchKeys.some((k) => {
      const v = item[k];
      return typeof v === 'string' && v.toLowerCase().includes(s);
    });
  });

  // V1.0.33: Build dữ liệu gom nhóm cho table tree expandable
  // Mỗi nhóm = 1 row "header" với children là các item thật
  const groupedTreeData = (() => {
    if (!groupBy || !groupingEnabled) return null;

    // Gom theo group key
    const map = new Map<string, { label: string; color?: string; items: T[] }>();
    for (const item of filteredData) {
      const g = groupBy(item);
      const key = g?.key ?? '__none__';
      if (!map.has(key)) {
        map.set(key, {
          label: g?.label ?? 'Chưa phân nhóm',
          color: g?.color,
          items: [],
        });
      }
      map.get(key)!.items.push(item);
    }

    // Build flat list: mỗi group là 1 row có children
    const result: any[] = [];
    // Sort: nhóm có sortOrder/label, "Chưa phân nhóm" cuối
    const entries = Array.from(map.entries()).sort(([ka], [kb]) => {
      if (ka === '__none__') return 1;
      if (kb === '__none__') return -1;
      return 0;
    });
    for (const [key, g] of entries) {
      result.push({
        id: `__group__${key}`,
        __isGroup: true,
        __groupKey: key,
        __groupLabel: g.label,
        __groupColor: g.color,
        __groupCount: g.items.length,
        children: g.items,
      });
    }
    return result;
  })();

  const actionColumn: ColumnsType<T>[number] = {
    title: 'Thao tác',
    key: 'actions',
    width: 140,
    fixed: 'right',
    render: (_, record) => (
      <Space size={4}>
        <Tooltip title="Sửa">
          <Button
            type="text"
            size="small"
            icon={<Pencil size={14} />}
            onClick={() => {
              setEditing(record);
              setModalOpen(true);
            }}
          />
        </Tooltip>
        <Popconfirm
          title="Xóa mục này?"
          description={record.isSystem ? 'Đây là mục hệ thống, không thể xóa.' : 'Hành động này không thể hoàn tác.'}
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{ danger: true, disabled: !!record.isSystem }}
          disabled={!!record.isSystem}
          onConfirm={async () => {
            try {
              await onDelete(record.id);
              message.success('Đã xóa');
            } catch (e: any) {
              // V1.0.44: Hiển thị lỗi BE rõ ràng (FK constraint, dept có user, etc.)
              message.error(e.response?.data?.message ?? e.message ?? 'Xóa thất bại');
            }
          }}
        >
          <Tooltip title={record.isSystem ? 'Mục hệ thống không thể xóa' : 'Xóa'}>
            <Button
              type="text"
              size="small"
              danger
              disabled={!!record.isSystem}
              icon={<Trash2 size={14} />}
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    ),
  };

  const handleSubmit = async (values: T) => {
    try {
      if (editing) {
        await onUpdate(editing.id, values);
        message.success('Đã cập nhật');
      } else {
        await onCreate(values);
        message.success('Đã thêm mới');
      }
      setModalOpen(false);
      setEditing(null);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message ?? 'Thao tác thất bại');
    }
  };

  const handleImport = async (file: File) => {
    if (!onImport) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await onImport(file);
      setImportResult(result);
      if (result.failed === 0) {
        message.success(`Import thành công ${result.success}/${result.total} dòng`);
      } else {
        message.warning(`Import: ${result.success} thành công, ${result.failed} lỗi`);
      }
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message ?? 'Import thất bại');
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={title}
        subtitle={subtitle}
        extra={
          <>
            {extraActions}
            {onImport && (
              <Button icon={<UploadIcon size={14} />} onClick={() => setImportModalOpen(true)}>
                Import Excel
              </Button>
            )}
            {onExport && (
              <Button icon={<Download size={14} />} onClick={onExport}>
                Export
              </Button>
            )}
            <Button
              type="primary"
              icon={<Plus size={14} />}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              Thêm mới
            </Button>
          </>
        }
      />

      <div
        style={{
          background: '#fff',
          border: '1px solid #d4d9df',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid #eceef1',
            background: '#f6f7f8',
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Input
            placeholder="Tìm theo mã, tên..."
            prefix={<Search size={14} style={{ color: '#7d8a98' }} />}
            allowClear
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Space size={8}>
            <span style={{ fontSize: 12, color: '#5b6875' }}>Chỉ hiện active:</span>
            <Switch size="small" checked={showOnlyActive} onChange={setShowOnlyActive} />
          </Space>
          {groupBy && enableGroupingToggle && (
            <Space size={8}>
              <span style={{ fontSize: 12, color: '#5b6875' }}>Gom theo nhóm:</span>
              <Switch size="small" checked={groupingEnabled} onChange={setGroupingEnabled} />
            </Space>
          )}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#7d8a98' }}>
            {filteredData.length} / {data.length} mục
          </div>
        </div>

        {groupedTreeData ? (
          // V1.0.33: Render mode gom nhóm với expandable
          <Table
            columns={[...buildGroupedColumns(columns, actionColumn)] as any}
            dataSource={groupedTreeData}
            rowKey="id"
            size="middle"
            scroll={{ x: 900 }}
            pagination={false}
            expandable={{
              defaultExpandAllRows: true,
              expandRowByClick: false,
              rowExpandable: (record: any) => record.__isGroup === true,
            }}
            rowClassName={(record: any) => record.__isGroup ? 'group-header-row' : ''}
          />
        ) : (
          <Table
            columns={[...columns, actionColumn]}
            dataSource={filteredData}
            rowKey="id"
            size="middle"
            scroll={{ x: 900 }}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Tổng ${t} mục` }}
          />
        )}
      </div>

      {/* Modal Add/Edit */}
      <Modal
        title={editing ? `Sửa: ${editing.name}` : `Thêm ${title.toLowerCase()} mới`}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        footer={null}
        destroyOnClose
        width={560}
      >
        {editing?.isSystem && (
          <Alert
            type="warning"
            showIcon
            icon={<AlertTriangle size={14} />}
            message="Đây là mục hệ thống, một số trường không thể chỉnh sửa."
            style={{ marginBottom: 16 }}
          />
        )}
        {renderForm({
          initialValues: editing ?? undefined,
          onFinish: handleSubmit,
          isSystem: editing?.isSystem,
        })}
      </Modal>

      {/* Modal Import */}
      <Modal
        title="Import từ Excel"
        open={importModalOpen}
        onCancel={() => {
          setImportModalOpen(false);
          setImportResult(null);
        }}
        footer={null}
        width={600}
      >
        <Alert
          type="info"
          showIcon
          message="Định dạng file Excel"
          description={
            <div>
              File .xlsx có các cột: <code>code</code>, <code>name</code>, <code>description</code>, <code>sortOrder</code>, <code>isActive</code>.
              <br />
              Mã trùng sẽ cập nhật, mã mới sẽ thêm. Dòng lỗi sẽ báo chi tiết.
            </div>
          }
          style={{ marginBottom: 16 }}
        />

        <Upload.Dragger
          accept=".xlsx,.xls,.csv"
          beforeUpload={(file) => {
            handleImport(file);
            return false;
          }}
          showUploadList={false}
          disabled={importing}
        >
          <div style={{ padding: 24 }}>
            <FileSpreadsheet size={40} style={{ color: '#7d8a98' }} />
            <div style={{ fontSize: 13, marginTop: 10 }}>
              {importing ? 'Đang xử lý...' : 'Kéo thả file hoặc bấm để chọn'}
            </div>
            <div style={{ fontSize: 11, color: '#adb6c0', marginTop: 4 }}>
              Hỗ trợ .xlsx, .xls, .csv · Tối đa 10 MB
            </div>
          </div>
        </Upload.Dragger>

        {importResult && (
          <div style={{ marginTop: 16 }}>
            <Space>
              <Tag color="success">✓ Thành công: {importResult.success}</Tag>
              <Tag color={importResult.failed > 0 ? 'error' : 'default'}>
                ✗ Lỗi: {importResult.failed}
              </Tag>
              <Tag>Tổng: {importResult.total}</Tag>
            </Space>
            {importResult.errors.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  maxHeight: 200,
                  overflow: 'auto',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 12,
                }}
              >
                {importResult.errors.map((err, i) => (
                  <div key={i} className="mono" style={{ color: '#991b1b' }}>
                    Dòng {err.row}: {err.code ? `[${err.code}] ` : ''}{err.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
